/**
 * QMS Badge Components
 * Reusable status and workflow badges for ticket cards
 */

import { useTranslation } from 'react-i18next';
import { Clock, Shield, CheckCircle, XCircle, Edit } from 'lucide-react';
import type { QmsTicket, QmsSeverityLevel } from '../../types/qms.types';

interface QmsWorkflowBadgeProps {
  ticket: QmsTicket;
}

/**
 * Workflow Badge - Shows QD approval or CEO review status
 */
export function QmsWorkflowBadge({ ticket }: QmsWorkflowBadgeProps) {
  // CEO review (higher priority)
  if (ticket.requires_ceo_review) {
    if (ticket.ceo_review_status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
          <Shield className="h-3 w-3" />
          CEO
        </span>
      );
    }
    if (
      ticket.ceo_review_status === 'confirmed' ||
      ticket.ceo_review_status === 'priority_adjusted'
    ) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-600 text-white">
          <CheckCircle className="h-3 w-3" />
          CEO
        </span>
      );
    }
  }

  // QD approval
  if (ticket.qd_approval_status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
        <Clock className="h-3 w-3" />
        QD
      </span>
    );
  }
  if (ticket.qd_approval_status === 'approved') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-600 text-white">
        <CheckCircle className="h-3 w-3" />
        QD
      </span>
    );
  }
  if (ticket.qd_approval_status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-600 text-white">
        <XCircle className="h-3 w-3" />
        QD
      </span>
    );
  }
  if (ticket.qd_approval_status === 'changes_requested') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
        <Edit className="h-3 w-3" />
        QD
      </span>
    );
  }

  return null;
}

interface QmsSeverityBadgeProps {
  level: QmsSeverityLevel;
}

/**
 * Severity Badge - Shows L0 (info) or L2 (major) severity
 * L1 (standard) is default and not shown
 */
export function QmsSeverityBadge({ level }: QmsSeverityBadgeProps) {
  if (level === 0) {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
        L0
      </span>
    );
  }
  if (level === 2) {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
        L2
      </span>
    );
  }
  return null;
}

/**
 * Priority Indicator - Returns the CSS class for priority border color
 */
export function getPriorityClass(priority: number): string {
  const classes: Record<number, string> = {
    1: 'border-l-4 border-l-gray-300',
    2: 'border-l-4 border-l-blue-400',
    3: 'border-l-4 border-l-yellow-400',
    4: 'border-l-4 border-l-orange-500',
    5: 'border-l-4 border-l-red-600',
  };
  return classes[priority] || classes[3];
}

interface QmsCapaBadgeProps {
  type: string;
  needsCapa?: boolean;
}

/**
 * CAPA Badge - Shows when ticket is a CAPA or needs CAPA
 */
export function QmsCapaBadge({ type, needsCapa }: QmsCapaBadgeProps) {
  if (type === 'capa' || needsCapa) {
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-600 text-white">
        CAPA
      </span>
    );
  }
  return null;
}

interface QmsDepartmentBadgeProps {
  department: string;
}

/**
 * Department Badge - Shows abbreviated department name
 */
export function QmsDepartmentBadge({ department }: QmsDepartmentBadgeProps) {
  const { t } = useTranslation('qms');

  const departmentLabels: Record<string, string> = {
    product: t('departments.product'),
    sales_marketing: t('departments.sales_marketing'),
    finance: t('departments.finance'),
    it: t('departments.it'),
    hr: t('departments.hr'),
    quality: t('departments.quality'),
    operations: t('departments.operations'),
  };

  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700">
      {departmentLabels[department] || department}
    </span>
  );
}
